import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Calendar, MapPin, ChevronDown, ChevronUp, Users, CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { resolveFileUrl } from '../../utils/fileUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Static featured past event - परिचय संगम 2025
const featuredPastEvent = {
  id: 'parichay-sangam-2025',
  title: 'परिचय संगम 2025',
  date: '2025-05-31',
  endDate: '2025-06-01',
  location: 'बालोतरा, राजस्थान',
  isPast: true,
  isFeatured: true,
  summary: 'युवक-युवतियों के सगाई संबंध होने में आ रही कठिनाइयां, युवाओं के विवाह देरी से होने के कारण, परिणाम व निवारण पर विशेष चर्चा।',
  description: `युवक-युवतियों के सगाई संबंध होने में आ रही कठिनाइयां, युवाओं के विवाह देरी से होने के कारण, परिणाम व निवारण पर विशेष चर्चा, विशेषज्ञों व स्वयं युवाओं की परिचयात्मक अभिव्यक्तियां आदि आकर्षणों के साथ परिचय संगम 2025 महासभा द्वारा बालोतरा समाज के सहयोग से बालोतरा में 31 मई व 1 जून, 2025 को आयोजित किया गया।

कार्यक्रम पूर्ण रूप से डिजिटलाइज्ड रहा व कार्यक्रम का लाइव टेलीकास्ट भी किया गया। कार्यक्रम जिस रुप में हुआ, समाज बंधुओं ने अभीभूत होकर इसे अद्वितीय, ऐतिहासिक व ग्लोबल करार दिया।`,
  stats: [
    { label: 'कुल पंजीकृत युवक-युवतियां', value: '515' },
    { label: 'उपस्थित प्रत्याशी', value: '372' },
    { label: 'पंजीकृत परिवार', value: '346' },
    { label: 'उपस्थित परिवार', value: '315' },
    { label: 'स्थानों से प्रतिभागिता', value: '140' },
  ],
  highlights: [
    'प्रत्याशियों विशेषतः महिला प्रत्याशियों ने प्रभावी परिचय दिया।',
    'पंजीकरण से कार्यक्रम की पूर्णता तक समाज की मातृशक्ति ने अलख जगाकर जागरूकता का संचार किया।',
    'अनेक स्थानों के अनेक कार्यकर्ताओं ने भी एक अभियान के रुप में कार्य कर एक उदाहरण प्रस्तुत किया।',
    'समाज के ही आई टी विशेषज्ञों ने अपने कौशल से कार्यक्रम को हाईटेक बना दिया।',
    'संगम मित्रों का आर्थिक योगदान उल्लेखनीय रहा।',
    'परिचय संगम के कार्यक्रम के बाद प्रत्याशियों की सगाई होने के समाचार प्राप्त होते रहे हैं।',
  ],
  learnings: [
    'प्रतिभागियों का सही विवरण प्राप्त कर उन्हें सही व्यवस्था प्रदान करना।',
    'समय पर सुव्यवस्थित कार्य संपन्न होना।',
    'सादा लेकिन पौष्टिक व स्वादिष्ट आहार निश्चित समय पर उपलब्ध होना व भोज्य पदार्थों को झूठा न छोड़ना। भोजन सामग्री न तो कम पड़ी व न ज्यादा। संतुलन बना रहा।',
    'प्रतिभाओं व युवाओं को आगे आने का अवसर मिला।',
    'टीम स्प्रीट से कार्य करना। सिस्टम ने काम किया। व्यक्तियों का योगदान रहा लेकिन श्रेय सिस्टम को देने का भाव रहा।',
  ],
  conclusion: 'आयोजन दीर्घावधि तक स्मरण में रहेगा व एक उदाहरण के रुप में पहचाना जायेगा। परिचय संगम का रिकॉर्ड व प्रत्याशियों की ईबुक अपडेट करने का काम टीम कर रही है।',
};

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [expandedEvent, setExpandedEvent] = useState(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      // Filter out the परिचय संगम 2025 from API events since we're showing it as featured
      const filteredEvents = response.data.filter(e => !e.title.includes('परिचय संगम 2025'));
      setEvents(filteredEvents);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('hi-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="gradient-orange py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-white/80 text-sm mb-6">
            <a href="/" className="hover:text-white">Home</a>
            <span>›</span>
            <span>Events</span>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Events / कार्यक्रम
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              महासभा के आयोजन एवं कार्यक्रम
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Featured Past Event - परिचय संगम 2025 */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-12"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6 section-heading flex items-center gap-2">
            <Calendar className="w-6 h-6 text-primary" />
            संपन्न कार्यक्रम / Past Events
          </h2>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
            {/* Event Header */}
            <div className="gradient-orange p-6 md:p-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <span className="inline-block bg-white/20 text-white text-xs font-semibold px-3 py-1 rounded-full mb-3">
                    ऐतिहासिक आयोजन
                  </span>
                  <h3 className="font-bold text-3xl md:text-4xl text-white mb-2">
                    {featuredPastEvent.title}
                  </h3>
                  <div className="flex flex-wrap items-center gap-4 text-white/80">
                    <div className="flex items-center gap-2">
                      <Calendar size={18} />
                      <span>31 मई - 1 जून, 2025</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin size={18} />
                      <span>{featuredPastEvent.location}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Event Summary */}
            <div className="p-6 md:p-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                {featuredPastEvent.description}
              </p>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
                {featuredPastEvent.stats.map((stat, index) => (
                  <div key={index} className="bg-gray-50 rounded-xl p-4 text-center">
                    <div className="text-2xl md:text-3xl font-bold text-primary mb-1">{stat.value}</div>
                    <div className="text-xs md:text-sm text-gray-500">{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* Expand/Collapse Button */}
              <button
                onClick={() => setExpandedEvent(expandedEvent === featuredPastEvent.id ? null : featuredPastEvent.id)}
                className="flex items-center gap-2 text-primary hover:text-primary-600 font-semibold mb-4"
              >
                {expandedEvent === featuredPastEvent.id ? (
                  <>
                    <ChevronUp size={20} />
                    <span>कम देखें</span>
                  </>
                ) : (
                  <>
                    <ChevronDown size={20} />
                    <span>विस्तार से देखें</span>
                  </>
                )}
              </button>

              {/* Expanded Content */}
              <AnimatePresence>
                {expandedEvent === featuredPastEvent.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    {/* Highlights */}
                    <div className="mb-8">
                      <h4 className="font-bold text-xl text-gray-900 mb-4 flex items-center gap-2">
                        <Users className="w-5 h-5 text-primary" />
                        मुख्य आकर्षण
                      </h4>
                      <div className="space-y-3">
                        {featuredPastEvent.highlights.map((highlight, index) => (
                          <div key={index} className="flex items-start gap-3 bg-green-50 p-3 rounded-lg">
                            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{highlight}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Learnings */}
                    <div className="mb-6">
                      <h4 className="font-bold text-xl text-gray-900 mb-4">
                        मार्गदर्शक सिद्धांत / Key Learnings
                      </h4>
                      <p className="text-gray-500 mb-4">
                        इस आयोजन की कार्यप्रणाली व प्रबंधन से निम्नलिखित तथ्य सामने आये, जो किसी भी सार्वजनिक आयोजन के लिये मार्गदर्शक सिद्धांत हो सकते हैं:
                      </p>
                      <div className="space-y-3">
                        {featuredPastEvent.learnings.map((learning, index) => (
                          <div key={index} className="flex items-start gap-3 border-l-4 border-primary pl-4 py-2">
                            <span className="font-bold text-primary">{index + 1}.</span>
                            <span className="text-gray-700">{learning}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Conclusion */}
                    <div className="bg-primary/5 p-6 rounded-xl border-l-4 border-primary">
                      <p className="text-lg text-gray-700 font-medium italic">
                        "{featuredPastEvent.conclusion}"
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.section>

        {/* Upcoming Events from API */}
        {events.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6 section-heading flex items-center gap-2">
              <Calendar className="w-6 h-6 text-primary" />
              आगामी कार्यक्रम / Upcoming Events
            </h2>

            <div className="space-y-4">
              {events.map((event, index) => (
                <motion.div 
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl p-6 border border-gray-100 shadow-card hover:shadow-card-hover transition-all flex flex-col md:flex-row gap-6"
                >
                  {event.image && (
                    <div className="w-full md:w-48 h-32 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                      <img 
                        src={resolveFileUrl(event.image)} 
                        alt={event.title} 
                        className="w-full h-full object-cover" 
                      />
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-primary font-semibold mb-2">
                      <Calendar size={16} />
                      <span className="text-sm">{formatDate(event.date)}</span>
                    </div>
                    <h3 className="font-bold text-xl text-gray-900 mb-2">{event.title}</h3>
                    <p className="text-gray-600 mb-3">{event.description}</p>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <MapPin size={16} />
                      <span><span className="font-semibold">स्थान:</span> {event.location}</span>
                    </div>
                    {event.title.includes('Khatri Conclave 2026') && (
                      <Link to="/conclave-registration">
                        <Button className="bg-primary hover:bg-primary-600 text-white font-semibold mt-4">
                          Register Now / पंजीकरण करें
                          <ArrowRight size={16} className="ml-2" />
                        </Button>
                      </Link>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.section>
        )}

        {/* No Events Message */}
        {events.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-center py-12 bg-gray-50 rounded-xl mt-8"
          >
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="font-bold text-xl text-gray-900 mb-2">
              आगामी कार्यक्रम शीघ्र घोषित होंगे
            </h3>
            <p className="text-gray-500">
              Upcoming events will be announced soon
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default EventsPage;
